import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import api, { API } from "../lib/api";
import { Textarea } from "../components/ui/textarea";

const SUGGESTIONS = [
  "I'm feeling really tired today",
  "Why is today's workout easy?",
  "I have to travel next week",
  "How am I progressing?",
];

function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && <div className="brand-gradient w-8 h-8 rounded-lg flex items-center justify-center font-heading font-black text-white text-sm shrink-0">C</div>}
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 leading-relaxed text-[15px] ${isUser ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card border border-border text-foreground rounded-bl-md"}`}>
        {children}
      </div>
    </motion.div>
  );
}

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/coach/messages").then((r) => setMessages(r.data.messages));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: msg, id: `local-${Date.now()}` }]);
    setStreaming("");
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("caden_token")}`,
        },
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setStreaming(full);
      }
      setMessages((m) => [...m, { role: "assistant", content: full, id: `a-${Date.now()}` }]);
      setStreaming("");
    } catch (e) {
      setStreaming("");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond just now. Try again.", id: `err-${Date.now()}` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] md:h-[calc(100vh-6rem)]">
      <div className="mb-4">
        <h1 className="font-heading font-black text-3xl tracking-tight text-foreground" data-testid="coach-title">Talk to Caden</h1>
        <p className="text-muted-foreground text-sm">Your coach knows your plan, your goal and your history.</p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="coach-messages">
        {messages.length === 0 && !streaming && (
          <div className="text-center py-10">
            <div className="brand-gradient w-14 h-14 rounded-2xl mx-auto flex items-center justify-center font-heading font-black text-white text-2xl mb-4">C</div>
            <p className="text-muted-foreground max-w-xs mx-auto">Tell me what's going on and I'll adjust. Or try one of these:</p>
            <div className="flex flex-wrap gap-2 justify-center mt-5">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid={`coach-suggestion-${s}`} onClick={() => send(s)}
                  className="px-4 py-2 rounded-full border border-border bg-card text-sm text-foreground hover:border-primary/50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => <Bubble key={m.id} role={m.role}>{m.content}</Bubble>)}
        {streaming && <Bubble role="assistant">{streaming}<span className="inline-block w-1.5 h-4 bg-primary ml-0.5 animate-pulse align-middle" /></Bubble>}
      </div>

      <div className="pt-4">
        <div className="flex items-end gap-2 bg-card border border-border rounded-2xl p-2">
          <Textarea
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Message your coach…"
            rows={1}
            className="border-0 focus-visible:ring-0 resize-none min-h-[44px] bg-transparent"
          />
          <button
            data-testid="coach-send"
            onClick={() => send()}
            disabled={sending || !input.trim()}
            className="brand-gradient w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0 disabled:opacity-40 active:scale-95 transition-transform"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
